import React, { useState, useEffect } from 'react'
import Sidebar from './Sidebar'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'
import Alert from '../shared/Alert'

const Users = () => {
  const [users, setUsers] = useState([])
  const [authToken, setAuthToken] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserRole, setNewUserRole] = useState('user')
  const [formError, setFormError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [scope, setScope] = useState(null)
  const [currentUserEmail, setCurrentUserEmail] = useState(null)
  const [hoveredRowId, setHoveredRowId] = useState(null)

  const fetchUsers = async () => {
    try {
      const response = await fetch('/collectors/api/users', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }

      const data = await response.json()
      setUsers(data.users)
      setAuthToken(data.auth_token)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchAuthInfo = async () => {
    const response = await fetch('/collectors/api/check_auth', {
      headers: {
        Accept: 'application/json',
      },
    })
    const data = await response.json()
    setScope(data.scope)
    setCurrentUserEmail(data.current_user.email)
  }

  useEffect(() => {
    fetchUsers()
    fetchAuthInfo()
  }, [])

  const handleAddUser = async e => {
    e.preventDefault()
    setFormError(null)
    setSuccessMessage(null)

    try {
      const response = await fetch('/collectors/api/users/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          user: {
            email: newUserEmail,
            law_firm_scope: newUserRole,
          },
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create user')
      }

      // Refresh users list
      await fetchUsers()

      // Reset form and show success message
      setNewUserEmail('')
      setNewUserRole('user')
      setSuccessMessage(`User added successfully. An invite has been sent to ${newUserEmail}.`)

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null)
      }, 5000)
    } catch (err) {
      setFormError(err.message)
    }
  }

  const handleRemoveUser = async userId => {
    if (!window.confirm('Are you sure you want to remove this user?')) {
      return
    }

    try {
      const response = await fetch('/collectors/api/users/remove', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          user_id: userId,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || 'Failed to remove user')
      }

      await fetchUsers()
    } catch (err) {
      setError(err.message)
    }
  }

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error: {error}</div>

  return (
    <div className='container-fluid'>
      <div className='row'>
        <Sidebar scope={scope} />
        <div className='col mb-10'>
          <div className='d-flex justify-content-between align-items-center mb-4'>
            <h1>Users</h1>
          </div>

          <div className='mb-4' style={{ maxWidth: '800px' }}>
            <h3 className='form-label'>Authentication Code</h3>
            <br />
            <p className='text-muted'>
              People in your organization can use this code to access your dashboard.
            </p>
            <input
              type='text'
              className='form-control'
              value={authToken}
              readOnly
              onClick={e => e.target.select()}
            />
          </div>

          <br />

          <div style={{ maxWidth: 'fit-content', marginBottom: '100px' }}>
            {successMessage && <Alert type='success'>{successMessage}</Alert>}
            {formError && (
              <tr>
                <td colSpan='3'>
                  <Alert type='danger'>{formError}</Alert>
                </td>
              </tr>
            )}

            <table className='table table-borderless'>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr
                    key={user.id}
                    onMouseEnter={() => setHoveredRowId(user.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    style={{ height: '80px' }}
                  >
                    <td>{user.email}</td>
                    <td>{user.law_firm_scope}</td>
                    <td>
                      {user.email !== currentUserEmail && hoveredRowId === user.id && (
                        <a
                          href='#'
                          onClick={e => {
                            e.preventDefault()
                            handleRemoveUser(user.id)
                          }}
                          aria-label='Remove user'
                        >
                          <svg
                            width='32'
                            height='32'
                            viewBox='0 0 41 42'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M35.5431 5.76562H5.07731C4.14254 5.76562 3.38477 6.5234 3.38477 7.45817V12.5358C3.38477 13.4706 4.14254 14.2284 5.07731 14.2284H35.5431C36.4779 14.2284 37.2357 13.4706 37.2357 12.5358V7.45817C37.2357 6.5234 36.4779 5.76562 35.5431 5.76562Z'
                              stroke='#9E9E9E'
                              strokeWidth='3.38509'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                            <path
                              d='M6.76953 14.2266V32.8446C6.76953 33.7423 7.12617 34.6034 7.761 35.2382C8.39583 35.873 9.25684 36.2297 10.1546 36.2297H30.4652C31.363 36.2297 32.224 35.873 32.8588 35.2382C33.4936 34.6034 33.8503 33.7423 33.8503 32.8446V14.2266'
                              stroke='#9E9E9E'
                              strokeWidth='3.38509'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                            <path
                              d='M16.0781 29.4588L24.5409 20.9961'
                              stroke='#9E9E9E'
                              strokeWidth='3.38509'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                            <path
                              d='M16.0781 20.9961L24.5409 29.4588'
                              stroke='#9E9E9E'
                              strokeWidth='3.38509'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
                <tr style={{ height: '48px' }}>
                  <td>
                    <FloatingLabel label='Email'>
                      <Form.Control
                        type='email'
                        placeholder='Enter email'
                        value={newUserEmail}
                        onChange={e => setNewUserEmail(e.target.value)}
                        required
                      />
                    </FloatingLabel>
                  </td>
                  <td>
                    <select
                      className='form-select'
                      value={newUserRole}
                      onChange={e => setNewUserRole(e.target.value)}
                      required
                    >
                      <option value='user'>User</option>
                      <option value='admin'>Admin</option>
                    </select>
                  </td>
                  <td>
                    <button className='btn btn-primary' onClick={handleAddUser}>
                      Add User
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Users
